import { useState, useEffect, useImperativeHandle, useRef, memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector, useDispatch } from "react-redux";
import Toolbar from "@mui/material/Toolbar";
import AppBar from "@mui/material/AppBar";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import SoftBadge from "components/SoftBadge";
import SoftTypography from "components/SoftTypography";
import FlowUsageDialog from "../FlowUsageDialog";
import TitlebarActions from "./components/TitlebarActions";
import SettingsMenu from "cxfcomponents/Navbars/DashboardNavbar/components/SettingsMenu";
import "react-toastify/dist/ReactToastify.css";
import { useMediaQuery } from "@mui/material";
import TitlebarFlowModeMenu from "./components/TitlebarActions/TitlebarFlowModeMenu";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FlowVersionsDialog from "layouts/Flow Designer/components/Titlebar/components/FlowVersionsDialog";
import CreateFlowVersionDialog from "layouts/Flow Designer/components/Titlebar/components/CreateFlowVersionDialog";
import DeleteFlowVersionDialog from "layouts/Flow Designer/components/Titlebar/components/DeleteFlowVersionDialog";
import ChangeFlowVersionDialog from "layouts/Flow Designer/components/Titlebar/components/ChangeFlowVersionDialog";
import useFlowVersions from "hooks/useFlowVersions";
import { updateUserPreferences } from "api/UserApi";
import { setDesignerSettings } from "../../../../redux/actions/flowDesigner/settingsActionCreator";
import { ThemedCheckIcon } from "assets/CXFIcons/Static";
import { useFlowPermissions } from "hooks/useFlowPermissions";
import { PORTAL_HEADER_HEIGHT } from "cxfcomponents/portalShell";

const statusColors = {
  active: "success",
  draft: "customInfo",
  inactive: "info",
  deleted: "error",
  test: "customInfo"
};

const Titlebar = ({ savedDateTime, handleUndo, handleRedo, ref }) => {
  const { t } = useTranslation("flowDesigner");
  const { user } = useAuth0();
  const { status } = useSelector((store) => store?.designer?.tabs?.currentFlowTab);
  const viewOptions = useSelector((store) => store?.designer?.settings?.viewOptions);
  const isVerticalCanvasDirection = useSelector(
    (store) => store?.designer?.settings?.isVerticalCanvasDirection
  );
  const dispatch = useDispatch();
  const { changeMode } = useFlowVersions(null, false);
  const [allowUndo, setAllowUndo] = useState(false);
  const [allowRedo, setAllowRedo] = useState(false);
  const viewOptionLoader = useRef({ initialized: false, settingsLoaded: false });
  const [currentStatus, setCurrentStatus] = useState(status);
  const [flowModeMenu, setFlowModeMenu] = useState(null);
  const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
  const mediaMatches = useMediaQuery("(max-width: 1280px)");
  const { userPreferences } = useSelector((store) => store.portal.users);
  const [showSavedDateTime, setShowSavedDateTime] = useState(!!savedDateTime);
  const { canActivate } = useFlowPermissions(currentSavedFlow);

  const handleChangeFlowMode = (event) => {
    event.preventDefault();
    setFlowModeMenu(event.currentTarget);
  };

  const handleChangeFlowModeMenuClose = () => {
    setFlowModeMenu(null);
  };

  const FlowModeContentBadge = useCallback(() => {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box>
          {/* {t(`statuses.${currentStatus === "test" ? "draft" : currentStatus}`)}&nbsp;
          {currentSavedFlow ? `v${currentSavedFlow?.current_version}` : ""} */}
          Version&nbsp;v{currentSavedFlow?.current_version}
        </Box>
        <ArrowDropDownIcon />
      </Box>
    );
  }, [currentSavedFlow?.current_version, currentStatus]);

  const handleToggleFlowMode = async () => {
    const newStatus = currentStatus === "active" ? "draft" : "active";
    await changeMode();
    setCurrentStatus(newStatus);
  };

  useImperativeHandle(ref, () => ({
    setUndo(enableUndo) {
      setAllowUndo(enableUndo);
    },
    setRedo(enableRedo) {
      setAllowRedo(enableRedo);
    }
  }));

  useEffect(() => {
    if (!viewOptionLoader.current.initialized) {
      setTimeout(() => {
        viewOptionLoader.current.initialized = true;
      }, 1000);
      return;
    }

    const updatePreferences = async () => {
      await updateUserPreferences(user?.cxfUserId, user?.stripeId, {
        designer: {
          viewOptions,
          isVerticalCanvasDirection
        }
      });
    };

    if (viewOptionLoader.current.initialized) {
      updatePreferences();
    }
  }, [isVerticalCanvasDirection, viewOptions]);

  useEffect(() => {
    if (userPreferences?.designer) {
      dispatch(setDesignerSettings(userPreferences.designer));
    }
  }, []);

  useEffect(() => {
    if (currentSavedFlow?.shared_status) {
      setCurrentStatus(currentSavedFlow.shared_status);
    }
  }, [currentSavedFlow?.shared_status]);

  useEffect(() => {
    if (savedDateTime) {
      setShowSavedDateTime(true);
      const timer = setTimeout(() => {
        setShowSavedDateTime(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [savedDateTime]);

  return (
    <AppBar className="titlebar-wrapper" position="static">
      <Container maxWidth="xl" className="header-container">
        <Toolbar disableGutters sx={{ minHeight: `${PORTAL_HEADER_HEIGHT}px !important`, height: PORTAL_HEADER_HEIGHT }}>
          <Box
            sx={{
              alignItems: "center",
              flexGrow: 1,
              display: "flex",
              flexDirection: "row",
              minWidth: 0,
              paddingLeft: "24px",
              justifyContent: mediaMatches ? "space-between" : "normal"
            }}
          >
            <Box className="titlebar-title-row">
              <SoftTypography
                sx={{
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  flexShrink: 1,
                  minWidth: 0
                }}
                variant="body2"
                color="text"
                mr={2}
                fontWeight="normal"
                title={currentSavedFlow?.name}
              >
                {currentSavedFlow?.name}
              </SoftTypography>
              {currentSavedFlow && (
                <>
                  <SoftBadge
                    variant="contained"
                    size="xs"
                    color={statusColors[currentStatus === "test" ? "draft" : currentStatus]}
                    container
                    badgeContent={<FlowModeContentBadge />}
                    onClick={handleChangeFlowMode}
                    sx={{ cursor: "pointer" }}
                  />
                  <TitlebarFlowModeMenu
                    state={flowModeMenu}
                    close={handleChangeFlowModeMenuClose}
                  />
                  {canActivate && (
                    <Tooltip
                      title={
                        currentStatus === "active"
                          ? t("tooltips.switch_to_draft")
                          : t("tooltips.switch_to_active")
                      }
                    >
                      <button
                        className={`btn-toggle-flow-mode ${currentStatus === "active" ? "active" : "draft"}`}
                        onClick={handleToggleFlowMode}
                      >
                        {currentStatus === "active" ? "Active" : "Draft"}
                      </button>
                    </Tooltip>
                  )}
                </>
              )}
            </Box>
            {currentSavedFlow && <TitlebarActions allowRedo={allowRedo} allowUndo={allowUndo} handleUndo={handleUndo} handleRedo={handleRedo} />}
            <Box
              className="btn-auto-save"
              sx={{
                display: "flex",
                width: "300px",
                alignItems: "center",
                opacity: showSavedDateTime ? 1 : 0,
                justifyContent: "flex-end"
              }}
              ml={2}
            >
              <SoftTypography variant="caption" color="text" fontWeight="regular" mr={1}>
                {`Saved ${savedDateTime}`}
              </SoftTypography>
              <ThemedCheckIcon />
            </Box>
            <Box
              sx={{
                marginLeft: "auto",
                order: {
                  sm: "2"
                }
              }}
            >
              <SettingsMenu />
            </Box>
          </Box>
        </Toolbar>

        <FlowUsageDialog />
        <FlowVersionsDialog />
        <CreateFlowVersionDialog />
        <DeleteFlowVersionDialog />
        <ChangeFlowVersionDialog />
      </Container>
    </AppBar>
  );
};

export default memo(Titlebar);
